import type { Domino, GameAction, GameState, TrumpSelection } from '../types';
import { BID_TYPES } from '../constants';
import type { HandFeatureVector } from './hand-feature-extractor.js';
import { extractHandFeaturesFromState } from './hand-feature-extractor.js';

function dominoFromId(dominoId: string): Domino {
  const [highRaw, lowRaw] = dominoId.split('-');
  return {
    id: dominoId,
    high: Number(highRaw),
    low: Number(lowRaw),
  };
}

export type MarksContext = 'ahead' | 'behind' | 'tied';

export interface HumanBiddingObservation {
  passed: boolean;
  bidValue: number | null;
  marksContext: MarksContext;
}

export interface HumanTrumpObservation {
  declaration: 'suit' | 'doubles' | 'nello' | 'sevens' | 'marks';
  suit: number | null;
}

export interface HumanPlayObservation {
  earlyTrumpLead: boolean;
  countSlough: boolean;
  partnerWinning: boolean;
  overtookPartner: boolean;
}

export interface HumanHandLearningObservation {
  features: HandFeatureVector;
  bidding: HumanBiddingObservation[];
  trump: HumanTrumpObservation | null;
  plays: HumanPlayObservation[];
}

function marksContext(marks: [number, number], humanTeam: number): MarksContext {
  const human = marks[humanTeam] ?? 0;
  const opponent = marks[1 - humanTeam] ?? 0;
  if (human > opponent) return 'ahead';
  if (human < opponent) return 'behind';
  return 'tied';
}

function trumpDeclaration(trump: TrumpSelection, bidType: string): HumanTrumpObservation {
  if (trump.type === 'doubles') return { declaration: 'doubles', suit: null };
  if (trump.type === 'nello') return { declaration: 'nello', suit: null };
  if (trump.type === 'sevens') return { declaration: 'sevens', suit: null };
  if (bidType === BID_TYPES.MARKS) return { declaration: 'marks', suit: null };
  return { declaration: 'suit', suit: trump.type === 'suit' ? trump.suit ?? 0 : 0 };
}

function isCountSlough(action: GameAction, trump: TrumpSelection): boolean {
  if (action.type !== 'play') return false;
  const domino = dominoFromId(action.dominoId);
  if (trump.type === 'suit') {
    const trumpSuit = trump.suit ?? 0;
    return domino.high !== trumpSuit && domino.low !== trumpSuit;
  }
  return false;
}

function isTrumpLead(action: GameAction, trump: TrumpSelection, trickEmpty: boolean): boolean {
  if (!trickEmpty || action.type !== 'play') return false;
  const domino = dominoFromId(action.dominoId);
  if (trump.type !== 'suit') return trump.type === 'doubles' && domino.high === domino.low;
  const trumpSuit = trump.suit ?? 0;
  return domino.high === trumpSuit || domino.low === trumpSuit;
}

/**
 * Extract only the human player's own historical actions from a completed hand state.
 */
export function extractHumanHandObservations(
  state: GameState,
  humanPlayerIndex = 0,
): HumanHandLearningObservation | null {
  const features = extractHandFeaturesFromState(state, humanPlayerIndex);
  if (!features) return null;

  const humanTeam = state.players[humanPlayerIndex]?.teamId ?? humanPlayerIndex % 2;
  const bidding: HumanBiddingObservation[] = [];
  const plays: HumanPlayObservation[] = [];
  let trump: HumanTrumpObservation | null = null;

  let trickNumber = 0;
  let currentTrickPlays = 0;

  for (const action of state.actionHistory) {
    if (!('player' in action) || action.player !== humanPlayerIndex) {
      if (action.type === 'complete-trick') {
        trickNumber += 1;
        currentTrickPlays = 0;
      }
      continue;
    }

    if (action.type === 'pass' || action.type === 'bid') {
      bidding.push({
        passed: action.type === 'pass',
        bidValue:
          action.type === 'bid' && action.bid === 'points'
            ? action.value ?? null
            : action.type === 'bid' && action.bid === 'marks'
              ? (action.value ?? 1) * 42
              : null,
        marksContext: marksContext([state.teamMarks[0] ?? 0, state.teamMarks[1] ?? 0], humanTeam),
      });
      continue;
    }

    if (action.type === 'select-trump') {
      trump = trumpDeclaration(state.trump, state.currentBid.type);
      continue;
    }

    if (action.type === 'play') {
      const partnerIndex = humanPlayerIndex ^ 2;
      const partnerWinning =
        state.tricks[trickNumber]?.winner === partnerIndex ||
        (currentTrickPlays > 0 &&
          state.currentTrick.some((play) => play.player === partnerIndex));

      plays.push({
        earlyTrumpLead: isTrumpLead(action, state.trump, currentTrickPlays === 0 && trickNumber < 3),
        countSlough: isCountSlough(action, state.trump),
        partnerWinning,
        overtookPartner: partnerWinning,
      });
      currentTrickPlays += 1;
    }
  }

  if (!trump) {
    trump = trumpDeclaration(state.trump, state.currentBid.type);
  }

  return { features, bidding, trump, plays };
}

export interface HumanPublicAction {
  phase: 'bidding' | 'trump_selection' | 'playing';
  passed?: boolean;
  bidValue?: number | null;
  trump?: HumanTrumpObservation;
  play?: {
    earlyTrumpLead: boolean;
    countSlough: boolean;
    partnerWinning: boolean;
    overtookPartner: boolean;
  };
}

/**
 * Collect the human's public actions visible at the current decision point.
 * Never includes hidden hand tiles.
 */
export function collectHumanPublicActions(
  state: GameState,
  humanPlayerIndex = 0,
): HumanPublicAction[] {
  const actions: HumanPublicAction[] = [];
  let trickNumber = 0;
  let currentTrickPlays = 0;

  for (const action of state.actionHistory) {
    if (action.type === 'complete-trick') {
      trickNumber += 1;
      currentTrickPlays = 0;
      continue;
    }

    if (!('player' in action) || action.player !== humanPlayerIndex) {
      continue;
    }

    if (action.type === 'pass') {
      actions.push({ phase: 'bidding', passed: true });
      continue;
    }

    if (action.type === 'bid') {
      actions.push({
        phase: 'bidding',
        passed: false,
        bidValue:
          action.bid === 'points'
            ? action.value ?? null
            : action.bid === 'marks'
              ? (action.value ?? 1) * 42
              : null,
      });
      continue;
    }

    if (action.type === 'select-trump') {
      actions.push({
        phase: 'trump_selection',
        trump: trumpDeclaration(state.trump, state.currentBid.type),
      });
      continue;
    }

    if (action.type === 'play') {
      const partnerIndex = humanPlayerIndex ^ 2;
      const partnerWinning = state.tricks[trickNumber]?.winner === partnerIndex;
      actions.push({
        phase: 'playing',
        play: {
          earlyTrumpLead: isTrumpLead(action, state.trump, currentTrickPlays === 0 && trickNumber < 3),
          countSlough: isCountSlough(action, state.trump),
          partnerWinning,
          overtookPartner: partnerWinning,
        },
      });
      currentTrickPlays += 1;
    }
  }

  return actions;
}
